import React from "react"
import { useStyletron } from "baseui"
import Add from "~/components/Icons/Add"
import { DesignEditorContext } from "~/contexts/DesignEditor"
import { nanoid } from "nanoid"
import { getDefaultTemplate } from "~/constants/design-editor"
import { useEditor } from "@agenteresolve/react"
import { Block } from "baseui/block"
import { TimerContext } from "@layerhub-io/use-timer"
import { IScene } from "@agenteresolve/types"
import TimelineItems from "./TimelineItems"
import TimeMarker from "./TimeMarker"
import TimelineControl from "./TimelineControl"
import TimelineContextMenu from "./TimelineContextMenu"
import useContextMenuTimelineRequest from "~/hooks/useContextMenuTimelineRequest"
import { findSceneIndexByTime } from "~/views/DesignEditor/utils/scenes"
import { renderScenePreview } from "~/utils/previews"

export default function () {
  const { time, setTime, status } = React.useContext(TimerContext)
  const {
    setScenes,
    setCurrentScene,
    currentScene,
    scenes,
    setCurrentPreview,
    setCurrentDesign,
    currentDesign,
    maxTime,
    displayAnimation,
  } = React.useContext(DesignEditorContext)
  const contextMenuTimelineRequest = useContextMenuTimelineRequest()
  const editor = useEditor()
  const [css] = useStyletron()
  const scrollRef = React.useRef<HTMLDivElement>(null)

  // Keep the playhead visible while playing/scrubbing long timelines.
  React.useEffect(() => {
    const el = scrollRef.current
    if (!el) return
    const x = (time || 0) / 40
    const margin = 140
    if (x < el.scrollLeft + margin) {
      el.scrollLeft = Math.max(0, x - margin)
    } else if (x > el.scrollLeft + el.clientWidth - margin) {
      el.scrollLeft = x - el.clientWidth + margin
    }
  }, [time])

  React.useEffect(() => {
    let watcher = async () => {
      const updatedTemplate = editor.scene.exportToJSON()
      const updatedPreview = await renderScenePreview(editor, updatedTemplate)
      setCurrentPreview(updatedPreview)
    }
    if (editor) {
      editor.on("history:changed", watcher)
    }
    return () => {
      if (editor) {
        editor.off("history:changed", watcher)
      }
    }
  }, [editor])

  React.useEffect(() => {
    if (editor) {
      if (currentScene) {
        updateCurrentScene(currentScene)
      } else {
        const defaultTemplate = getDefaultTemplate({
          width: 1200,
          height: 1200,
        })

        editor.scene
          .importFromJSON(defaultTemplate)
          .then(() => {
            // SET INITIAL DURATION
            setCurrentDesign({
              id: nanoid(),
              frame: defaultTemplate.frame,
              metadata: {},
              name: "Untitled Design",
              preview: "",
              scenes: [],
              type: "VIDEO",
            })
            const initialDesign = editor.scene.exportToJSON() as any
            editor.renderer.render(initialDesign).then((data) => {
              setCurrentScene({ ...initialDesign, preview: data, duration: 5000 })
              setScenes([{ ...initialDesign, preview: data, duration: 5000 }])
            })
          })
          .catch(console.log)
      }
    }
  }, [editor, currentScene])

  const updateCurrentScene = React.useCallback(
    async (design: IScene) => {
      await editor.scene.importFromJSON(design)
      const updatedPreview = await renderScenePreview(editor, design)
      setCurrentPreview(updatedPreview)
    },
    [editor, currentScene]
  )

  const addScene = React.useCallback(async () => {
    setCurrentPreview("")
    const updatedTemplate = editor.scene.exportToJSON()
    const updatedPreview = await renderScenePreview(editor, updatedTemplate)

    const maxTime = scenes.reduce(function (previousVal, currentValue) {
      return previousVal + currentValue.duration!
    }, 0)

    const defaultTemplate = getDefaultTemplate(currentDesign.frame)
    const newPreview = await renderScenePreview(editor, defaultTemplate)
    const newPage = {
      ...defaultTemplate,
      id: nanoid(),
      preview: newPreview,
      duration: 5000,
    } as any
    setScenes((prev) => {
      const updatedPages = prev.map((p) => {
        if (p.id === updatedTemplate.id) {
          return { ...updatedTemplate, preview: updatedPreview, duration: p.duration }
        }
        return p
      })
      return [...updatedPages, newPage] as any[]
    })
    setTime(maxTime)
  }, [scenes, currentDesign, setScenes, setTime])

  const changePage = React.useCallback(
    async (page: any) => {
      setCurrentPreview("")
      if (editor) {
        const updatedTemplate = editor.scene.exportToJSON()
        const updatedPreview = await renderScenePreview(editor, updatedTemplate)

        setScenes((prev) =>
          prev.map((p) => {
            if (p.id === updatedTemplate.id) {
              return { ...updatedTemplate, preview: updatedPreview, duration: p.duration }
            }
            return p
          })
        )
        setCurrentScene(page)
      }
    },
    [editor, setScenes, setCurrentScene]
  )

  // In edit mode the canvas follows the playhead (switching the edited scene
  // as the timeline crosses scene boundaries). In animation mode the canvas is
  // the HyperFrames preview, so switching scenes here would only cause heavy
  // re-renders and preview reloads mid-playback.
  React.useEffect(() => {
    if (editor && scenes && currentScene && status !== "RUNNING" && !displayAnimation) {
      const currentSceneIndex = findSceneIndexByTime(scenes, time)
      const currentIndex = scenes.findIndex((page) => page.id === currentScene.id)
      if (currentSceneIndex !== currentIndex && scenes[currentSceneIndex]) {
        changePage(scenes[currentSceneIndex])
      }
    }
  }, [editor, scenes, time, currentScene, status, displayAnimation])

  return (
    <Block $style={{ display: "flex", alignItems: "center", flex: 1, minWidth: 0, overflow: "hidden" }}>
      <TimelineControl />
      <Block $style={{ background: "#ffffff", flex: 1, minWidth: 0, overflow: "hidden" }}>
        <div className={css({ display: "flex", alignItems: "center", minWidth: 0, width: "100%" })}>
          {/* The timeline scrolls horizontally on its own; long timelines must
              not stretch the editor (the canvas would go off-screen). */}
          <div
            ref={scrollRef}
            style={{
              overflowX: "auto",
              overflowY: "hidden",
              flex: 1,
              minWidth: 0,
              scrollbarWidth: "thin",
            }}
          >
            <Block
              id="TimelineItemsContainer"
              onClick={(e: React.MouseEvent<HTMLElement>) => {
                const rect = (e.currentTarget as HTMLElement).getBoundingClientRect()
                const clickX = e.clientX - rect.left
                const newTime = Math.max(0, Math.min(clickX * 40, (maxTime || 0) - 1))
                if (typeof setTime === "function") setTime(newTime)
              }}
              $style={{
                display: "flex",
                alignItems: "center",
                position: "relative",
                padding: "1rem 0",
                width: "max-content",
                minWidth: "100%",
              }}
            >
              {contextMenuTimelineRequest.visible && <TimelineContextMenu />}
              <TimeMarker />
              <TimelineItems />
            </Block>
          </div>
          <Block
            onClick={addScene}
            $style={{
              width: "100px",
              height: "56px",
              background: "rgb(243,244,246)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
            }}
          >
            <Add size={20} />
          </Block>
        </div>
      </Block>
    </Block>
  )
}
